import React from 'react';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import { withTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import MenuItem from '@material-ui/core/MenuItem';

import Button from 'components/common/Buttons/Button';
import InputField from 'components/common/InputField/InputField';
import { createEmailValidationFn } from 'utils/validations/validations';
import InputFieldAutocomplete from 'components/common/InputField/InputFieldAutocomplete';
import ThreeppModal from 'components/common/Modal/Modal';
import { getFormattedName } from 'selectors/formatName/selector';
import { showSuccessMessage, showErrorMessage, showNeutralNotification } from 'components/common/Alert/Alert';
import { getIdFromUrlParams } from 'selectors/assessment/selector';
import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import { getQueryFilterVariables } from 'selectors/assessmentFilters/selector';
import { listUsers, getAssessmentById, getCurrentUser, inviteUser, addParticipantToAssessment, setRoleForParticipant } from 'graphql/operations';
import { isEmptyString, normalizeEmail, extractEmailDomain, hasValue } from 'utils/functions';
import { CACHE_ONLY } from 'graphql/fetchPolicies';
import EventEmitter from 'utils/events';
import { DeviceWidth, EventEmitterTypes } from 'utils/constants';
import { SIDEBAR_TOGGLE_DURATION } from '../../../../utils/constants';

import './InviteParticipantModal.scss';

const ROLE_OWNER = { key: 'OWNER', label: 'Owner' };
const ROLE_CONTRIBUTOR = { key: 'CONTRIBUTOR', label: 'Contributor' };

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  position: '',
  role: ROLE_CONTRIBUTOR.key,
  emailError: null,
  isProcessing: false,
  roles: [],
  isMobile: false
};

export class InviteParticipantModal extends React.Component {
  static defaultProps = {
    toggle: k => k
  };

  constructor(props) {
    super(props);
    const currentUserEmail = props.currentUser.email;
    const domain = extractEmailDomain(currentUserEmail);
    const validEmailDomains = [domain];
    this.validateEmail = createEmailValidationFn(validEmailDomains, true);
    this.state = { ...INITIAL_STATE };
  }

  getIsMobile = () => window.innerWidth <= DeviceWidth.MOBILE;

  // TODO: Figure out what's supposed to be going on here - this makes no sense - state.isMobile is never used...
  // resize = () => this.setState({ isMobile: this.getIsMobile() });
  resize = () => {};

  componentDidMount() {
    this.resize();
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
    clearTimeout();
  }

  onOpened = () => {
    const { owners, currentUser, editUser } = this.props;
    const currentUserIsOwner = owners.findIndex(owner => owner.email === currentUser.email) !== -1;

    const editUserIsOwner = editUser ? owners.findIndex(owner => owner.email === editUser.email) !== -1 : false;

    const roles = [];

    // only owners can add other owners
    if (currentUserIsOwner) {
      // if only owner, then no option for change to contributor
      if (owners.length === 1 && editUserIsOwner) {
        roles.push(ROLE_OWNER);
      } else {
        // many owners, no restrictions.
        roles.push(ROLE_CONTRIBUTOR);
        roles.push(ROLE_OWNER);
      }
    } else {
      // not an owner, only option is contributor.
      roles.push(ROLE_CONTRIBUTOR);
    }

    this.setState({
      ...INITIAL_STATE,
      email: editUser ? editUser.email : '',
      firstName: editUser ? editUser.firstName : '',
      lastName: editUser ? editUser.lastName : '',
      position: editUser ? editUser.position : '',
      role: editUser && editUserIsOwner ? ROLE_OWNER.key : ROLE_CONTRIBUTOR.key,
      roles
    });
  };

  close = () => {
    const { isProcessing } = this.state;
    const { toggle, openSidebar } = this.props;
    toggle(openSidebar);
    const isMobile = this.getIsMobile();
    if (isMobile) {
      const timeout = isProcessing ? SIDEBAR_TOGGLE_DURATION : 0;
      this.toggleSidebar(timeout);
    }
  };

  toggleSidebar = timeout => {
    const { openSidebar } = this.props;
    setTimeout(() => {
      EventEmitter.dispatch(EventEmitterTypes.TOGGLE_MOBILE_SIDEBAR, { right: 'right', open: openSidebar === true });
    }, timeout);
  };

  setFirstName = ({ key, label }) => {
    const { allUsers } = this.props;
    let stateValue = {};
    if (key) {
      // autocomplete used, pre-fill other inputs
      const user = allUsers.find(u => u.email === key);

      stateValue = {
        position: user.position,
        email: key,
        firstName: user.firstName,
        lastName: user.lastName
      };
    }
    this.setState({ firstName: label, ...stateValue });
  };

  setLastName = ({ key, label }) => {
    const { allUsers } = this.props;
    let stateValue = {};
    if (key) {
      // autocomplete used, pre-fill other inputs
      const user = allUsers.find(u => u.email === key);

      stateValue = {
        position: user.position,
        email: key,
        firstName: user.firstName,
        lastName: user.lastName
      };
    }
    this.setState({ lastName: label, ...stateValue });
  };

  setEmail = ({ key, label }) => {
    const { allUsers } = this.props;
    let stateValue = {};
    if (key) {
      // autocomplete used, pre-fill other inputs
      const user = allUsers.find(u => u.email === key);

      stateValue = {
        position: user.position,
        nameFieldInitialValue: getFormattedName({ user }),
        firstName: user.firstName,
        lastName: user.lastName
      };
    }

    this.setState({
      ...stateValue,
      email: label,
      emailError: null
    });
  };

  handleBlur = event => {
    const { t } = this.props;
    const normalizedEmail = normalizeEmail(event.target.value);
    const error = this.validateEmail(normalizedEmail);
    this.setState({ emailError: error && t(error.key, { ...error.args }) });
  };

  setPosition = value => this.setState({ position: value });

  setRole = value => this.setState({ role: value });

  onSave = () => {
    const { participants, editUser, allUsers, t } = this.props;
    const { email } = this.state;
    const emailAlreadyParticipant = participants.findIndex(p => normalizeEmail(p.email) === normalizeEmail(email)) !== -1;

    if (!editUser && emailAlreadyParticipant) {
      showNeutralNotification(t('assessment.participantsDialog.userAlreadyParticipant'));
    } else {
      this.setState({ isProcessing: true }, () => {
        if (editUser) {
          this.updateParticipantRole();
        } else {
          const userForEmail = allUsers.find(u => u.email.trim().toLowerCase() === email.trim().toLowerCase());

          if (userForEmail) {
            // user exists, adding user to assessment
            this.inviteExistingParticipant(userForEmail);
          } else {
            // user doesn't exist. inviting to create account.
            this.inviteNewParticipant();
          }
        }
      });
    }
  };

  updateParticipantRole = () => {
    const { editUser, assessmentId, setRoleForParticipant: propsSetRoleForParticipant, t } = this.props;
    const { role } = this.state;

    propsSetRoleForParticipant({
      variables: {
        assessmentId,
        email: editUser.email,
        role
      }
    })
      .then(() => {
        showSuccessMessage(t('assessment.participantsDialog.setRoleForParticipant.successMsg'));

        this.close();
      })
      .catch(() => {
        showErrorMessage(t('assessment.participantsDialog.setRoleForParticipant.errorMsg'));
        this.setState({ isProcessing: false });
      });
  };

  inviteNewParticipant = () => {
    const { assessmentId, inviteUser: propsInviteUser, propsAssignParticipant, t } = this.props;
    const { firstName, lastName, email, position, role } = this.state;

    propsInviteUser({
      variables: {
        assessmentId,
        email: normalizeEmail(email),
        role,
        firstName,
        lastName,
        position: hasValue(position) && position.trim() === '' ? null : position,
        country: 'USA'
      }
    })
      .then(() => {
        if (propsAssignParticipant) {
          this.assignParticipant(email);
        }
        showSuccessMessage(t('assessment.participantsDialog.inviteUser.successMsg'));
        this.close();
      })
      .catch(() => {
        showErrorMessage(t('assessment.participantsDialog.inviteUser.errorMsg'));
        this.setState({ isProcessing: false });
      });
  };

  inviteExistingParticipant() {
    const { addParticipantToAssessment: propsAddParticipantToAssessment, assessmentId, propsAssignParticipant, t } = this.props;
    const { role, email } = this.state;

    propsAddParticipantToAssessment({
      variables: {
        assessmentId,
        email: normalizeEmail(email),
        role
      }
    })
      .then(() => {
        if (propsAssignParticipant) {
          this.assignParticipant(email);
        }
        showSuccessMessage(t('assessment.participantsDialog.addParticipantToAssessment.successMsg'));
        this.close();
      })
      .catch(() => {
        showErrorMessage(t('assessment.participantsDialog.addParticipantToAssessment.errorMsg'));
        this.setState({ isProcessing: false });
      });
  }

  assignParticipant(email) {
    const { onAssigneesChanged } = this.props;
    const addedParticipant = [normalizeEmail(email)];
    onAssigneesChanged(addedParticipant, []);
  }

  isAddDisabled() {
    const { editUser, owners } = this.props;
    const { firstName, lastName, email, role, isProcessing } = this.state;
    const normalizedEmail = normalizeEmail(email);

    if (editUser) {
      const isOwner = owners.findIndex(u => u.email.trim() === editUser.email.trim()) !== -1;
      return isOwner ? role === ROLE_OWNER.key : role === ROLE_CONTRIBUTOR.key;
    }
    return (
      isEmptyString(firstName) ||
      isEmptyString(lastName) ||
      isEmptyString(email) ||
      isEmptyString(role) ||
      isProcessing === true ||
      this.validateEmail(normalizedEmail) !== null
    );
  }

  render() {
    const { editUser, isOpen, toggle, nameSuggestions, emailSuggestions, t } = this.props;
    const { firstName, lastName, email, emailError, position, role, roles, isProcessing } = this.state;

    let modalTitle;
    let saveButtonText;
    let disableNewUserInputs;

    if (editUser) {
      disableNewUserInputs = true;
      modalTitle = t('assessment.participantsDialog.titleUpdate');
      saveButtonText = t('common.save');
    } else {
      disableNewUserInputs = false;
      modalTitle = t('assessment.participantsDialog.titleCreate');
      saveButtonText = t('common.add');
    }

    return (
      <ThreeppModal onOpened={this.onOpened} isOpen={isOpen} toggle={toggle} classes="invite-participants-modal" header={modalTitle} size="sm">
        <InputFieldAutocomplete
          id="addParticipant-firstName"
          label={t('assessment.participantsDialog.firstName')}
          fullWidth
          disabled={disableNewUserInputs}
          suggestions={nameSuggestions}
          onChange={this.setFirstName}
          value={firstName}
        />

        <InputFieldAutocomplete
          id="addParticipant-lastName"
          label={t('assessment.participantsDialog.lastName')}
          fullWidth
          disabled={disableNewUserInputs}
          suggestions={nameSuggestions}
          onChange={this.setLastName}
          value={lastName}
        />

        <InputFieldAutocomplete
          id="addParticipant-email"
          t={t}
          label={t('assessment.participantsDialog.fieldEmail')}
          fullWidth
          disabled={disableNewUserInputs}
          suggestions={emailSuggestions}
          onChange={this.setEmail}
          onBlur={this.handleBlur}
          value={email}
          errorMsg={emailError}
        />

        <InputField
          id="addParticipant-jobTitle"
          label={t('assessment.participantsDialog.fieldPosition')}
          fullWidth
          disabled={disableNewUserInputs}
          autoComplete="organization-title"
          value={position || ''}
          onChange={this.setPosition}
        />

        <InputField
          id="addParticipant-fieldRole"
          select
          fullWidth
          label={t('assessment.participantsDialog.fieldRole')}
          value={role}
          onChange={this.setRole}
        >
          {roles.map(option => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </InputField>

        <div className="action-row">
          <Button testId="addParticipant-cancel" className="btn-cancel" buttonStyle="destructive" onClick={this.close}>
            {t('common.cancel')}
          </Button>
          <Button
            testId="addParticipant-add"
            className="btn-add"
            buttonStyle="primary"
            onClick={this.onSave}
            loading={isProcessing}
            disabled={this.isAddDisabled()}
          >
            {saveButtonText}
          </Button>
        </div>
      </ThreeppModal>
    );
  }
}

export const InviteParticipantModalHOC = compose(
  withRouter,
  withTranslation(),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  graphql(listUsers),
  graphql(setRoleForParticipant, { name: 'setRoleForParticipant' }),
  graphql(inviteUser, {
    options: props => ({
      refetchQueries: () => [
        {
          query: getAssessmentById,
          variables: {
            id: getIdFromUrlParams(props),
            fieldsFilter: getQueryFilterVariables(props)
          }
        },
        'listUsers'
      ],
      awaitRefetchQueries: true
    }),
    name: 'inviteUser'
  }),
  graphql(addParticipantToAssessment, {
    options: props => ({
      refetchQueries: () => [
        {
          query: getAssessmentById,
          variables: {
            id: getIdFromUrlParams(props),
            fieldsFilter: getQueryFilterVariables(props)
          }
        }
      ],
      awaitRefetchQueries: true
    }),
    name: 'addParticipantToAssessment'
  }),
  graphql(getAssessmentById, {
    options: props => ({
      variables: {
        id: getIdFromUrlParams(props)
      },
      fetchPolicy: CACHE_ONLY
    }),
    name: 'getAssessmentById'
  }),
  mapProps(props => {
    const assessment = props.getAssessmentById.getAssessmentById || {};
    return {
      t: props.t,
      editUser: props.editUser,
      openSidebar: props.openSidebar,
      participants: assessment.participants || [],
      owners: assessment.owners || [],
      toggle: props.toggle,
      isOpen: props.isOpen,
      allUsers: props.data.listUsers || [],
      nameSuggestions: (props.data.listUsers || []).map(u => ({ key: u.email, label: `${getFormattedName({ user: u })}` })),
      emailSuggestions: (props.data.listUsers || []).map(u => ({ key: u.email, label: u.email })),
      inviteUser: props.inviteUser,
      addParticipantToAssessment: props.addParticipantToAssessment,
      setRoleForParticipant: props.setRoleForParticipant,
      onAssigneesChanged: props.onAssigneesChanged,
      propsAssignParticipant: props.assignParticipant,
      currentUser: getCurrentUserSelector(props),
      assessmentId: getIdFromUrlParams(props)
    };
  })
);

export default InviteParticipantModalHOC(InviteParticipantModal);
